// vehicle-modal.js — Vehicle selector popup
// Requires vehicle-garage.js to be loaded first (provides VehicleData, VsDropdown, GarageStore).
// NOTE: vehicle-modal.js may execute before vehicle-garage.js (both deferred, sections render
// before theme footer snippets). VsDropdown is therefore initialised lazily inside open().

/* global VehicleData, VsDropdown, GarageStore */

if (!customElements.get('vehicle-popup')) {
  class VehiclePopup extends HTMLElement {
    connectedCallback() {
      // Retry if children (dialog) are not yet in the DOM.
      if (!this.querySelector('dialog')) {
        requestAnimationFrame(() => this.connectedCallback());
        return;
      }

      this.dialog   = this.querySelector('dialog');
      this.closeBtn = this.querySelector('.js-vp-close');
      this.ctaBtn   = this.querySelector('.js-vp-cta');
      this.statusEl = this.querySelector('.vp-status');

      // _drops is initialised lazily in open() — VsDropdown may not be defined yet here.
      this._drops     = null;
      this._hierarchy = null;
      this._loaded    = false;
      this._timer     = null;
      this._scrollFn  = null;
      this._exitFn    = null;

      this._bindEvents();
      this._initTrigger();
      this._prefetch();
    }

    disconnectedCallback() {
      clearTimeout(this._timer);
      if (this._scrollFn) { window.removeEventListener('scroll', this._scrollFn); this._scrollFn = null; }
      if (this._exitFn)   { document.removeEventListener('mouseleave', this._exitFn); this._exitFn = null; }
    }

    // ── Trigger ───────────────────────────────────────────────────────────────

    _initTrigger() {
      // In the theme editor: open immediately and clear any stale dismissed flag.
      if (window.Shopify?.designMode) {
        sessionStorage.removeItem('vp-dismissed');
        this.open();
        return;
      }

      const showOnce = this.dataset.showOnce !== 'false';
      if (showOnce && sessionStorage.getItem('vp-dismissed')) return;

      const trigger = this.dataset.trigger || 'timer';

      if (trigger === 'timer') {
        this._timer = setTimeout(() => this.open(), Number(this.dataset.delay) || 3000);

      } else if (trigger === 'scroll') {
        const threshold = (Number(this.dataset.scrollPercent) || 40) / 100;
        this._scrollFn = () => {
          const scrollable = document.body.scrollHeight - window.innerHeight;
          if (scrollable <= 0) return;
          if (window.scrollY / scrollable >= threshold) {
            window.removeEventListener('scroll', this._scrollFn);
            this._scrollFn = null;
            this.open();
          }
        };
        window.addEventListener('scroll', this._scrollFn, { passive: true });

      } else if (trigger === 'exit') {
        this._exitFn = (e) => {
          if (e.clientY > 0) return;
          document.removeEventListener('mouseleave', this._exitFn);
          this._exitFn = null;
          this.open();
        };
        document.addEventListener('mouseleave', this._exitFn);
      }
      // trigger='none' → opened via open() call or [data-opens-vehicle-popup]
    }

    // ── Open / close ──────────────────────────────────────────────────────────

    open() {
      if (!this.dialog || this.dialog.open) return;
      this._initDrops(); // safe here — vehicle-garage.js is loaded by the time any trigger fires
      this.dialog.showModal();
      if (!this._loaded) this._loadData();
    }

    close() {
      if (!this.dialog) return;
      this.dialog.close();
      if (this.dataset.showOnce !== 'false') sessionStorage.setItem('vp-dismissed', '1');
    }

    // ── Lazy VsDropdown init ──────────────────────────────────────────────────

    _initDrops() {
      if (this._drops) return;
      this._drops = {
        year:  this._makeDrop('[data-vp-year]'),
        make:  this._makeDrop('[data-vp-make]'),
        model: this._makeDrop('[data-vp-model]'),
      };
      if (this._drops.year)  this._drops.year._onChange  = () => this._onYearChange();
      if (this._drops.make)  this._drops.make._onChange  = () => this._onMakeChange();
      if (this._drops.model) this._drops.model._onChange = () => this._onModelChange();
    }

    _makeDrop(selector) {
      const el = this.querySelector(selector);
      return el ? new VsDropdown(el) : null;
    }

    // ── Events ────────────────────────────────────────────────────────────────

    _bindEvents() {
      this.closeBtn?.addEventListener('click', () => this.close());
      this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.close(); });
      this.ctaBtn?.addEventListener('click', () => this._onSubmit());

      // Can't find your vehicle toggle
      const toggle  = this.querySelector('.js-vp-cant-find-toggle');
      const body    = this.querySelector('#vp-contact-wrap');
      const form    = this.querySelector('.js-vp-contact-form');
      const statusEl = this.querySelector('.js-vp-contact-status');

      toggle?.addEventListener('click', () => {
        const expanded = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!expanded));
        body.hidden = expanded;
      });

      // Use robust selector — Shopify's {% form %} tag may not preserve custom classes
      const resolvedForm = form || this.querySelector('.vp-cant-find__body form');
      resolvedForm?.addEventListener('submit', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const email   = resolvedForm.querySelector('[name="contact[email]"]');
        const vehicle = resolvedForm.querySelector('[name="contact[body]"]');
        if (!email?.value || !vehicle?.value) {
          this._setContactStatus('Please fill in your email and vehicle info.', 'error', statusEl);
          return;
        }
        const submitBtn = resolvedForm.querySelector('.vp-contact-form__submit');
        submitBtn.disabled = true;

        // Submit via hidden iframe — avoids fetch bot-detection (hCaptcha) while keeping
        // the user on the current page.
        const iframeName = 'vp-contact-iframe-' + Date.now();
        const iframe = document.createElement('iframe');
        iframe.name = iframeName;
        iframe.style.cssText = 'position:absolute;width:0;height:0;border:0;visibility:hidden;';
        document.body.appendChild(iframe);

        resolvedForm.target = iframeName;

        iframe.addEventListener('load', () => {
          try {
            const landed = iframe.contentWindow?.location?.href || '';
            if (landed.includes('contact_posted=true') || landed.includes('/contact')) {
              resolvedForm.hidden = true;
              sessionStorage.setItem('vp-dismissed', '1');
              this._setContactStatus('Thanks! We\'ll be in touch soon.', 'success', statusEl);
              setTimeout(() => this.close(), 2000);
            } else {
              throw new Error('unexpected redirect');
            }
          } catch {
            // cross-origin guard — if we can't read the URL, assume success
            resolvedForm.hidden = true;
            sessionStorage.setItem('vp-dismissed', '1');
            this._setContactStatus('Thanks! We\'ll be in touch soon.', 'success', statusEl);
            setTimeout(() => this.close(), 2000);
          }
          iframe.remove();
          resolvedForm.target = '';
        }, { once: true });

        resolvedForm.submit();
      });
    }

    // ── Data ──────────────────────────────────────────────────────────────────

    _prefetch() {
      // Wait for page load so we don't compete with LCP/FCP resources, then use
      // idle time to avoid long tasks on the main thread.
      const run = () => {
        if (typeof VehicleData === 'undefined') { setTimeout(run, 100); return; }
        VehicleData.getHierarchy()
          .then(h => { this._hierarchy = h; })
          .catch(() => {});
      };
      const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1));
      if (document.readyState === 'complete') {
        idle(run);
      } else {
        window.addEventListener('load', () => idle(run), { once: true });
      }
    }

    async _loadData() {
      if (typeof VehicleData === 'undefined') {
        console.error('[vehicle-popup] VehicleData not found. Ensure vehicle-garage.js loads before vehicle-modal.js.');
        this._setStatus('Configuration error — please refresh.', true);
        return;
      }

      try {
        // _prefetch() may have already populated this._hierarchy. If not, fetch now.
        if (!this._hierarchy) {
          this._hierarchy = await VehicleData.getHierarchy();
        }

        this._loaded = true;

        if (!Object.keys(this._hierarchy).length) {
          this._drops.year?.reset('No vehicles found');
          this._setStatus('No vehicle data found.', true);
          return;
        }

        const years = VehicleData.getYears(this._hierarchy);
        this._drops.year?.setOptions(years.map(y => ({ value: y, label: y })));
      } catch (err) {
        this._drops.year?.reset('Error — please refresh');
        this._setStatus('Could not load vehicle data. Please refresh.', true);
        console.error('[vehicle-popup]', err);
      }
    }

    // ── Cascade ───────────────────────────────────────────────────────────────

    _onYearChange() {
      const year = this._drops.year?.value;
      this._drops.make?.reset('Select Make');
      this._drops.model?.reset('Select Model');
      if (this.ctaBtn) this.ctaBtn.disabled = true;
      if (!year) return;
      this._drops.make?.setOptions(VehicleData.getMakes(this._hierarchy, year).map(m => ({ value: m.slug, label: m.name })));
    }

    _onMakeChange() {
      const year = this._drops.year?.value;
      const make = this._drops.make?.value;
      this._drops.model?.reset('Select Model');
      if (this.ctaBtn) this.ctaBtn.disabled = true;
      if (!year || !make) return;
      this._drops.model?.setOptions(VehicleData.getModels(this._hierarchy, year, make).map(m => ({ value: m.slug, label: m.name })));
    }

    _onModelChange() {
      if (this.ctaBtn) this.ctaBtn.disabled = !this._drops.model?.value;
    }

    // ── Submit ────────────────────────────────────────────────────────────────

    _onSubmit() {
      const year      = this._drops.year?.value;
      const makeSlug  = this._drops.make?.value;
      const modelSlug = this._drops.model?.value;
      if (!year || !makeSlug || !modelSlug) return;

      const makeEntry  = VehicleData.getMakes(this._hierarchy, year).find(m => m.slug === makeSlug);
      const modelEntry = VehicleData.getModels(this._hierarchy, year, makeSlug).find(m => m.slug === modelSlug);
      const collection = modelEntry?.collection || null;

      if (typeof GarageStore !== 'undefined') {
        GarageStore.add({
          year, makeSlug,
          make:       makeEntry?.name  || makeSlug,
          modelSlug,
          model:      modelEntry?.name || modelSlug,
          collection,
        });
      }

      sessionStorage.setItem('vp-dismissed', '1');

      if (collection) {
        window.location.href = `/collections/${collection}`;
      } else {
        const url = new URL(this.dataset.redirect || '/collections/all', window.location.origin);
        url.searchParams.set('filter.p.tag', `fit:${year}:${makeSlug}:${modelSlug}`);
        window.location.href = url.toString();
      }
    }

    // ── Helpers ───────────────────────────────────────────────────────────────

    _setStatus(msg, isError = false) {
      if (!this.statusEl) return;
      this.statusEl.textContent = msg;
      this.statusEl.hidden      = !msg;
      this.statusEl.className   = `vp-status${isError ? ' vp-status--error' : ''}`;
    }

    _setContactStatus(msg, type, el) {
      if (!el) return;
      el.textContent = msg;
      el.hidden      = !msg;
      el.className   = `vp-contact-status vp-contact-status--${type}`;
    }
  }

  customElements.define('vehicle-popup', VehiclePopup);
}

// Any element with [data-opens-vehicle-popup] opens the popup on click.
document.addEventListener('click', (e) => {
  if (e.target.closest('[data-opens-vehicle-popup]')) {
    document.querySelector('vehicle-popup')?.open();
  }
});
